import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { HorizontalFlex, PadContainer } from 'smartsuite-ui';
import { inputTypes, predicateValues } from '../shared/constants/constants';
import FilterString from '../shared/filtering/FilterString';
import FilterSingleSelect from '../shared/filtering/FilterSingleSelect';
import FilterMultiSelect from '../shared/filtering/FilterMultiSelect';
import FilterUnconstrainedMultiSelect from '../shared/filtering/FilterUnconstrainedMultiSelect';
import FilterTreeSelect from '../shared/filtering/FilterTreeSelect';
import {
    getResetCascadeChildFilter,
    shouldShowFilter,
    getFilterOptions,
    getAlwaysVisibleFilterKeys,
} from '../shared/filtering/filtersModalUtils';
import { AdditionalFilterSelector } from '../shared/filtering/AdditionalFilterSelector';
import { removeFromArray } from '../viewDashboards/widgets/utils/dataFormatUtils';
import { useLabelContext } from '../shared/hooks/labelContext';
/* eslint-disable complexity */
export default function BuildFilters(props) {
    const { predicateLabels } = useLabelContext();

    const additionalFilterKeys = Object.keys(
        _.pickBy(props.filterConfigurations, (filter) => filter.isAdditional)
    );

    const requiredFilterKeys = Object.keys(
        _.pickBy(props.filterConfigurations, (filter) => filter.isRequired)
    );

    const [usedAdditionalFilterKeys, setUsedAdditionalFilterKeys] = useState(
        additionalFilterKeys.filter((filterKey) =>
            _.keys(props.filterValues).includes(filterKey)
        )
    );

    function handleAdditionalFilterSelect(selectedFilterOption) {
        if (selectedFilterOption) {
            setUsedAdditionalFilterKeys((prevState) => [
                ...prevState,
                selectedFilterOption.valueField,
            ]);
        }
    }
    function removeAdditionalFilter(filterKey) {
        return () => {
            const updatedFilterValues = { ...props.filterValues };
            delete updatedFilterValues[filterKey];

            props.saveFilters({
                ...updatedFilterValues,
                ...getResetCascadeChildFilter(
                    props.filterConfigurations[filterKey].cascadeChildKey,
                    updatedFilterValues,
                    predicateLabels
                ),
            });

            setUsedAdditionalFilterKeys((prevState) => {
                const updatedUsedFilterKeys = removeFromArray(
                    prevState,
                    prevState.indexOf(filterKey)
                );

                if (props.filterConfigurations[filterKey].cascadeChildKey) {
                    return removeFromArray(
                        updatedUsedFilterKeys,
                        updatedUsedFilterKeys.indexOf(
                            props.filterConfigurations[filterKey]
                                .cascadeChildKey
                        )
                    );
                }

                return updatedUsedFilterKeys;
            });
        };
    }

    function updateFilter(updatedFilter, cascadeChildKey) {
        props.saveFilters({
            ...props.filterValues,
            ...updatedFilter,
            ...getResetCascadeChildFilter(
                cascadeChildKey,
                props.filterValues,
                predicateLabels
            ),
        });
    }

    function buildFilterComponent(filterKey, isAdditionalFilter) {
        const filter = props.filterConfigurations[filterKey];
        const isRequired = requiredFilterKeys.includes(filterKey);
        const isAdditionalFilterUsed =
            usedAdditionalFilterKeys.includes(filterKey);

        const cascadeParentFilter = props.filterValues[filter.cascadeParentKey];
        const disabled = cascadeParentFilter
            ? cascadeParentFilter.values.length > 1 ||
              cascadeParentFilter.predicate.valueField === predicateValues.notIn
            : false;

        const options = getFilterOptions(
            props.filterConfigurations,
            filterKey,
            props.filterValues,
            props.selectOptions,
            requiredFilterKeys
        );

        if (
            !shouldShowFilter(filter, options, isRequired) ||
            (isAdditionalFilter && !isAdditionalFilterUsed)
        ) {
            return null;
        }

        const selectProps = {
            availableFilter: filter,
            disabled,
            filter: props.filterValues[filterKey],
            filterKey,
            isAdditionalFilter,
            isRequired,
            key: filterKey,
            options,
            removeAdditionalFilter,
            showAsterisk: false,
            updateFilter,
        };

        if (filter.type === inputTypes.string) {
            return <FilterString {...selectProps} />;
        }
        if (filter.type === inputTypes.singleSelect) {
            return <FilterSingleSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.multiSelect) {
            return <FilterMultiSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.unconstrainedMultiSelect) {
            return <FilterUnconstrainedMultiSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.treeSelect) {
            return <FilterTreeSelect {...selectProps} />;
        }

        return null;
    }

    function buildVisibleFilterComponent(filterKey) {
        return buildFilterComponent(filterKey, false);
    }
    function buildAdditionalFilterComponent(filterKey) {
        return buildFilterComponent(filterKey, true);
    }

    if (_.isEmpty(props.filterConfigurations)) {
        return null;
    }

    const alwaysVisibleFilterKeys = getAlwaysVisibleFilterKeys(
        props.filterConfigurations,
        additionalFilterKeys
    );
    const visibleFilterComponents = alwaysVisibleFilterKeys
        .map(buildVisibleFilterComponent)
        .filter((x) => x);
    const additionalFilterComponents = usedAdditionalFilterKeys
        .map(buildAdditionalFilterComponent)
        .filter((x) => x);

    const populatedAdditionalFilterKeys = additionalFilterKeys.filter(
        (filterKey) =>
            shouldShowFilter(
                props.filterConfigurations[filterKey],
                getFilterOptions(
                    props.filterConfigurations,
                    filterKey,
                    props.filterValues,
                    props.selectOptions,
                    requiredFilterKeys
                ),
                requiredFilterKeys.includes(filterKey)
            )
    );

    if (
        _.isEmpty(visibleFilterComponents) &&
        !populatedAdditionalFilterKeys.length
    ) {
        return null;
    }

    return (
        <>
            <PadContainer
                cssClassModifiers={['pad-medium', 'no-pad-left-right']}
            >
                <HorizontalFlex
                    cssClassModifiers={['flex-wrap', 'align-flex-end']}
                >
                    {visibleFilterComponents}
                    {additionalFilterComponents}
                    <AdditionalFilterSelector
                        additionalFilterKeys={populatedAdditionalFilterKeys}
                        availableFilters={props.filterConfigurations}
                        handleAdditionalFilterSelect={
                            handleAdditionalFilterSelect
                        }
                        isEditor={false}
                        selectOptions={props.selectOptions}
                        usedAdditionalFilterKeys={usedAdditionalFilterKeys}
                    />
                </HorizontalFlex>
            </PadContainer>
            <hr className="divider divider--criteria" />
        </>
    );
}
/* eslint-enable complexity */

BuildFilters.propTypes = {
    filterConfigurations: PropTypes.instanceOf(Object),
    filterValues: PropTypes.instanceOf(Object),
    saveFilters: PropTypes.func.isRequired,
    selectOptions: PropTypes.instanceOf(Array),
};
